import type {
  ContractClause,
  ContractDocumentDownloadView,
  SharedContractDetailView,
  SharedObjectView,
} from "@closer/shared";
import {
  CONTRACT_DISPLAY_PHASE_NAMES,
  CONTRACT_DISPLAY_PHASES,
  CONTRACT_STATUS_NAMES,
  contractDisplayPhase,
  formatFileSize,
  OBJECT_SHARE_PERMISSION_NAMES,
  OBJECT_SHARE_PERMISSIONS,
} from "@closer/shared";
import { Badge, Button, Card, PageHeader, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";

import { ClauseReadOnlyList } from "../../components/ClauseReadOnlyList";
import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDate } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { ProviderDistributionSection } from "./ProviderDistributionSection";
import { paylasilanYorumVersiyonlari, SharedCommentsSection } from "./SharedCommentsSection";
import { SharedContractAccessSection } from "./SharedContractAccessSection";
import { paylasilanVersiyonlar, SharedNegotiationSection } from "./SharedNegotiationSection";
import {
  SharedReconciliationTasksSection,
  SharedSignatureTasksSection,
} from "./SharedPendingWorkSection";
import { SharedServiceProviderSection } from "./SharedServiceProviderSection";
import { useSharedCurrentVersion } from "./useSharedCurrentVersion";

/**
 * "BANA NE PAYLAŞILDI" GÖRÜNÜMÜ (CLOS-873; CLOS-154 `[140]`).
 *
 * ### ⚠ Bu bir SÖZLEŞME DETAYI DEĞİL — ve adı da öyle olmamalı
 *
 * Yük ölçüldü: `SharedContractDetailView` = başlık + sahip + paylaşım düzeyi + AÇIKÇA
 * paylaşılan çalışma nesneleri + nesne paylaşımları. İçerik, versiyon listesi, taraflar,
 * tarihler YOK — ve bu bir eksiklik değil, kartın kapsam sınırı: *"ortak projection yalnız
 * açıkça paylaşılan ... nesneleri içerir"*, *"katılmış olmak tüm özel not ve görevleri
 * otomatik açmaz"*. Ekranı "Sözleşme detayı" diye adlandırsaydık kullanıcı sözleşmenin
 * tamamını bekler, boş bir kabuk görür ve kusur sanardı. Başlık bu yüzden ne
 * paylaşıldığını anlatıyor.
 *
 * ### ⚠ Sahip adı ve paylaşım düzeyi AYNI YÜKTEN
 *
 * `SharedContractDetailView`, `SharedContractView`'ı genişletiyor; ayrı bir istek yok.
 */
export function SharedContractDetailPage() {
  const { contractId } = useParams();
  const { activeTenantId } = useAuth();
  const { preferences } = usePreferences();
  const tenantId = activeTenantId!;

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "paylasilan-sozlesme", contractId ?? "yok"),
    enabled: activeTenantId !== null && contractId !== undefined,
    queryFn: async () =>
      (await api<SharedContractDetailView>(`/api/shared-contracts/${contractId}`, { tenantId }))
        .data,
  });

  /*
    ⚠ `existenceIsSecret` VARSAYILANDA KALIYOR (true). Erişimi olmayan bir kullanıcıya
    "bu sözleşme var ama giremezsin" demek, varlığın kendisini sızdırır — kart bunu açıkça
    yasaklıyor: *"doğrudan nesne URL'si ... özel nesne varlığını sızdırmaz."* Sunucu da
    zaten erişimsizi `notFound` ile karşılıyor.
  */
  const hata = sorgu.error
    ? resolveErrorState(sorgu.error, {
        idempotent: true,
        overrides: {
          error: {
            title: "Paylaşılan sözleşme yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;

  if (sorgu.isPending) {
    return (
      <div role="status" aria-label="Paylaşılan sözleşme yükleniyor">
        <Skeleton lines={4} />
      </div>
    );
  }
  if (hata) return <StateView state={hata} onRetry={() => void sorgu.refetch()} />;

  const detay = sorgu.data!;
  const nesneler = detay.sharedObjects;
  const evre = contractDisplayPhase(detay.status);

  return (
    <section className="flex w-full flex-col gap-6">
      <PageHeader
        title={detay.title}
        /*
          ⚠ "SİZE AÇILAN BÖLÜMLER" DİLİ KALDIRILDI (Müvekkil Alanı remediation). Bu yüzey
          iki kaynağa hizmet ediyor: nesne/bölüm paylaşımı (haricî katılımcı) VE sözleşme
          düzeyindeki Müvekkil Alanı grant'i. İkincisinde "bölüm" diye bir şey yok; başlık
          kullanıcıya olmayan bir önkoşulu arattırıyordu.
        */
        description={`${detay.ownerName} paylaştı`}
        /*
          ⚠ ROZET FİİLİ İZİNLERDEN TÜRETİLİYOR — ikinci bir gerçek kaynağı yok.
          Gerekçesi `fiiliDuzey` yorumunda.
        */
        meta={
          nesneler.length > 0 ? <Badge tone="neutral">{fiiliDuzey(nesneler)}</Badge> : undefined
        }
      />

      {/*
        ⚠ İÇERİK YOKLUĞU BAĞLAM YOKLUĞU DEĞİL (Shared Detail §4.1). Kullanıcı sayfayı
        açtığında "hangi sözleşmedeyim, tarafları kim, süreç nerede?" sorularının cevabını
        metin paylaşılmamış olsa bile görmeli. Bu bölüm o üç soruyu karşılıyor.
      */}
      <Card>
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-fg-heading">Süreç aşaması</span>
            {/*
              ⚠ Evre TEK MERKEZÎ EŞLEMEDEN geliyor (`contractDisplayPhase`); ekran domain
              durumunu kendi yorumlamıyor ve bilinmeyen durumda evre UYDURMUYOR.
            */}
            {evre ? (
              <ol className="flex flex-wrap items-center gap-1" data-testid="surec-seridi">
                {CONTRACT_DISPLAY_PHASES.map((asama) => (
                  <li key={asama}>
                    <Badge tone={asama === evre ? "info" : "neutral"}>
                      {CONTRACT_DISPLAY_PHASE_NAMES[asama]}
                    </Badge>
                  </li>
                ))}
              </ol>
            ) : (
              <Badge tone="neutral">{CONTRACT_STATUS_NAMES[detay.status] ?? "Bilinmiyor"}</Badge>
            )}
          </div>
          <p className="text-sm text-fg-muted">
            Son güncelleme: {new Date(detay.updatedAt).toLocaleString("tr-TR")}
          </p>
        </div>
      </Card>

      {/*
        ⚠ HUKUKİ TARAFLAR — sunucudan gelen güvenli projeksiyon. Ad, tür ve saf; başka
        hiçbir şey. İç üyelik, erişim grant'i, departman ve sorumluluk bu yanıtta YOK.
      */}
      <Card>
        <h2 className="mb-3 text-base font-semibold text-fg-heading">Taraflar</h2>
        {detay.parties.length === 0 ? (
          <p data-testid="taraf-yok" className="text-sm text-fg-muted">
            Bu sözleşmede henüz hukuki taraf tanımlanmadı.
          </p>
        ) : (
          <ul className="flex flex-col gap-2" data-testid="taraflar">
            {detay.parties.map((taraf, i) => (
              <li key={`${taraf.displayName}-${i}`} className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-fg">{taraf.displayName}</span>
                <span className="text-sm text-fg-muted">
                  {taraf.entityType === "person"
                    ? "Gerçek kişi"
                    : taraf.entityType === "organization"
                      ? "Kurum"
                      : "Tür belirtilmedi"}
                </span>
                {taraf.side ? (
                  <Badge tone="neutral">
                    {taraf.side === "represented" ? "Temsil edilen taraf" : "Karşı taraf"}
                  </Badge>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h2 className="mb-3 text-base font-semibold text-fg-heading">Ayrıca paylaşılanlar</h2>
        {nesneler.length === 0 ? (
          <p data-testid="nesne-yok" className="text-sm text-fg-muted">
            Sözleşmenin tamamı yukarıda. Ayrıca paylaşılmış bir çalışma kalemi yok.
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {nesneler.map((nesne) => (
              <li
                key={nesne.shareId}
                data-testid="paylasilan-nesne"
                className="flex flex-col gap-2 rounded-md bg-muted p-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="neutral">{PAYLASIM_TURU_ADLARI[nesne.objectType]}</Badge>
                  {/*
                    ⚠ İzin METİNLERİ kendi sözlüğünden (CLOS-873 dönüşü). Ham `view` ve
                    `comment` Türkçe bir ekranda İngilizce enum sızıntısıydı. Sözlük
                    zaten vardı ve GÖNDEREN tarafta kullanılıyordu; aynı izin iki ekranda
                    iki ad taşıyordu.
                    ⚠ Hesap YAPILMIYOR: izinlerin kendisi sunucudan geliyor, ekran yalnız
                    adlandırıyor. Kendi izin hesabımız ikinci bir doğruluk kaynağı olurdu.
                  */}
                  <span className="text-xs text-fg-soft">
                    {nesne.permissions
                      .map(
                        (k) =>
                          OBJECT_SHARE_PERMISSION_NAMES[
                            k as keyof typeof OBJECT_SHARE_PERMISSION_NAMES
                          ] ?? k,
                      )
                      .join(" · ")}
                  </span>
                </div>

                {/*
                  ⚠ PAYLAŞILAN SÜRÜMÜN İÇERİĞİ GÖSTERİLİYOR (CLOS-873 dönüşü B4).
                  Yük `content.document.clauses` ile TAM madde metnini taşıyordu ve ekran
                  yalnız rozet basıyordu — paylaşmanın bütün amacı bu içerikti. Rozet
                  "sana bir versiyon paylaşıldı" der; hangi metin olduğunu söylemez.

                  ⚠ Metin BEYAZ-LİSTELİ ADAPTÖRDEN: paylaşılmayan hiçbir şey bu ekrana
                  gelmiyor, yani "sözleşmenin tamamı sızar mı" sorusunun cevabı sunucuda
                  ve burada tekrarlanmıyor.
                */}
                <PaylasilanMaddeler nesne={nesne} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <PaylasilanBelgeKarti contractId={contractId!} />

      {detay.sharedWorkItems.length > 0 ? (
        <Card>
          <h2 className="mb-3 text-base font-semibold text-fg-heading">
            Paylaşılan not ve görevler
          </h2>
          <ul className="flex flex-col gap-2">
            {detay.sharedWorkItems.map((nesne) => (
              <li
                key={nesne.id}
                data-testid="calisma-nesnesi"
                className="flex flex-col gap-1 rounded-md bg-muted p-2"
              >
                <span className="text-xs text-fg-soft">
                  {nesne.itemType === "task" ? "Görev" : "Not"} · {nesne.createdByName} ·{" "}
                  {formatDate(nesne.createdAt, preferences.locale)}
                </span>
                <span className="text-sm text-fg">{nesne.body}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/*
        ⚠ BEKLEYEN İŞLER MÜZAKERENİN ÜSTÜNDE (CLOS-883): bunlar kullanıcıdan BİR KOMUT
        bekleyen kalemler; müzakere ve yorum ise okunacak içerik. Aşağı koysaydık,
        karşı taraf kendisinden beklenen imzayı görmek için sayfayı sonuna kadar kaydırırdı.
      */}
      {/* Kurum içi erişim dağıtımı (CLOS-1125 · 1157): kimin erişeceğini karşı kurum belirler. */}
      <SharedContractAccessSection contractId={contractId!} />
      <SharedReconciliationTasksSection contractId={contractId!} />
      <SharedSignatureTasksSection contractId={contractId!} />

      {/*
        ⚠ SAĞLAYICI KARTI TARAF LİSTESİNİN YANINDA, İÇİNDE DEĞİL (CLOS-1185). Sağlayıcı
        sözleşmenin tarafı değil; taraf kartına koysaydık ekran hukuki topolojiye avukatları
        da eklemiş olurdu. Kart yalnız kendi tarafı için çiziliyor ve karşı tarafın kiminle
        çalıştığı buradan hiç geçmiyor.
      */}
      <SharedServiceProviderSection contractId={contractId!} />

      {/*
        ⚠ SAĞLAYICI YÜZEYİNİN KENDİ KARTI. `servedPartyName` yalnız hizmet sağlayıcıya dolu
        geliyor; katılımcı organizasyon kendi adına hareket ettiği için orada boş ve kart
        hiç çizilmiyor. Büronun kimleri görevlendirdiği karşı tarafa giden hiçbir yüke
        girmiyor.
      */}
      <ProviderDistributionSection
        contractId={contractId!}
        servedPartyName={detay.servedPartyName ?? null}
      />

      <SharedNegotiationSection
        contractId={contractId!}
        paylasilanVersiyonlar={paylasilanVersiyonlar(nesneler)}
        kapsam={detay.capabilities ?? []}
      />

      {/*
        ⚠ YORUM YÜZEYİ `comment` İZNİNE BAĞLI (CLOS-886): bölüm, izinli versiyon yoksa
        kendini hiç çizmiyor. Kapı sunucuda ayrıca duruyor.
      */}
      <SharedCommentsSection
        contractId={contractId!}
        versiyonlar={paylasilanYorumVersiyonlari(nesneler)}
      />
    </section>
  );
}

function BelgeSatir({ etiket, deger }: { etiket: string; deger: React.ReactNode }) {
  return (
    <div className="flex flex-wrap gap-2 text-sm">
      <dt className="w-40 shrink-0 text-fg-soft">{etiket}</dt>
      <dd className="flex-1 text-fg">{deger}</dd>
    </div>
  );
}

/**
 * GÜNCEL SÜRÜMÜN BELGESİ (K4 · CLOS-1119).
 *
 * ### ⚠ KART YOKSA HATA DA YOK
 *
 * Bu yüzey YALNIZ Müvekkil Alanı temsilcisine açık; katılımcı organizasyon aynı ekranı
 * kullanıyor ve uçtan 404 alıyor. Hatayı gösterseydik, kendisi için hiç var olmayan bir
 * yetenek yüzünden ekranda kırmızı bir kutu görürdü — "bir şey bozuldu" der, oysa bozulan
 * bir şey yok. Belgesi olmayan sözleşme de aynı yola düşüyor: gösterilecek bir şey yoksa
 * kart hiç çizilmiyor. Sessizlik YALNIZ 404 için; başka hata kardeş bölümler gibi görünür.
 *
 * ### ⚠ İmzalı adres EKRANDA DURMUYOR
 *
 * Sahip tarafındaki `ContractDocumentPage` deseninin aynısı: indirme bir `<a href>` değil,
 * tıklandığı an istenen ve hemen bırakılan bir mutation. Adres state'e de girmiyor —
 * öyle olsaydı kullanıcı hiç tıklamasa bile DOM'da ve ekran paylaşımında dururdu.
 *
 * ### ⚠ Sürüm SEÇİLMİYOR
 *
 * Uç `:versionId` almıyor: alıcıya versiyon geçmişi açılmadı, dolayısıyla ekranın
 * seçebileceği bir sürüm kümesi de yok. "v{n}" başlığı hangi sürüme baktığını söylüyor —
 * kullanıcı sözleşmenin son hâlini gördüğünü bilmeli.
 */
function PaylasilanBelgeKarti({ contractId }: { contractId: string }) {
  const tenantId = useAuth().activeTenantId!;
  const toast = useToast();

  /** ⚠ Sorgu ORTAK kancada: aynı sürümü öneri yüzeyi de taban alıyor (kancanın gerekçesi). */
  const sorgu = useSharedCurrentVersion(contractId);

  /**
   * ⚠ Bilet bir MUTATION, sorgu değil: önbelleğe alınabilir olsaydı süresi dolmuş bir
   * adres tekrar tekrar sunulur, erişimi geri çekilmiş kullanıcıya da çalışan bir
   * bağlantı kalırdı.
   */
  const indirme = useMutation({
    mutationFn: async () =>
      (
        await api<ContractDocumentDownloadView>(
          `/api/shared-contracts/${contractId}/current-version/download-url`,
          { tenantId },
        )
      ).data,
    onSuccess: (bilet) => {
      const bag = document.createElement("a");
      bag.href = bilet.downloadUrl;
      bag.rel = "noopener noreferrer";
      if (bilet.fileName) bag.download = bilet.fileName;
      document.body.appendChild(bag);
      bag.click();
      bag.remove();
    },
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Belge indirilemedi"),
  });

  /**
   * ⚠ 404 KARTIN YOKLUĞU, ARIZA DEĞİL — ve YALNIZ 404. Bileşen başlığındaki gerekçe
   * burada uygulanıyor: sunucu "bu yüzey senin için yok" demek için `notFound` döndürüyor
   * ve ekran sessiz kalıyor. Başka her hata (sunucu, ağ) kardeş bölümlerle aynı yoldan
   * (`StateView`) gösteriliyor: temsilcinin belgesi VARKEN getirilemediyse bunu yutmak,
   * ona "paylaşılmamış" dedirtirdi. Ayrım `ExternalContractDetailPage` deseniyle aynı.
   */
  if (sorgu.isError) {
    if (sorgu.error instanceof ApiRequestError && sorgu.error.status === 404) return null;
    return (
      <StateView
        state={resolveErrorState(sorgu.error, {
          idempotent: true,
          overrides: {
            error: { title: "Belge getirilemedi", description: "Bölüm şu an getirilemedi." },
          },
        })}
        onRetry={() => void sorgu.refetch()}
      />
    );
  }
  if (!sorgu.data) return null;

  const onizleme = sorgu.data;
  const indirilebilir = onizleme.source.available;
  /** Metin yalnız çıkarım başarılıysa var; şekil sunucunun beyanı, ekran hesaplamıyor. */
  const maddeler =
    onizleme.extraction.status === "extracted" ? (onizleme.extraction.document?.clauses ?? []) : [];

  return (
    <Card
      title={`Belge — Versiyon v${onizleme.versionNumber}`}
      actions={
        indirilebilir ? (
          <Button
            onClick={() => indirme.mutate()}
            disabled={indirme.isPending}
            aria-label="Belgeyi indir"
          >
            {indirme.isPending ? "Hazırlanıyor…" : "İndir"}
          </Button>
        ) : undefined
      }
    >
      <dl className="flex flex-col gap-1">
        <BelgeSatir etiket="Dosya adı" deger={onizleme.source.fileName ?? "—"} />
        <BelgeSatir etiket="Biçim" deger={onizleme.source.contentType ?? "—"} />
        <BelgeSatir etiket="Boyut" deger={formatFileSize(onizleme.source.sizeBytes)} />
        {onizleme.extraction.pageCount !== null && (
          <BelgeSatir etiket="Sayfa sayısı" deger={String(onizleme.extraction.pageCount)} />
        )}
      </dl>
      {/*
        ⚠ ÖNİZLEME = METİN, künye değil. Kart yalnız dosya adı ve boyut gösterseydi
        "önizleme" sözü boş kalırdı; metin aynı sayfadaki paylaşılan sürüm deseniyle
        (`MaddeListesi`) çiziliyor. Sahip tarafının zengin blok çizimi değil: bu yüzeyin
        tek işi "sözleşme ne diyor" sorusuna cevap vermek.
      */}
      {maddeler.length > 0 && (
        <div className="mt-3">
          <ClauseReadOnlyList maddeler={maddeler} testId="belge-maddeler" />
        </div>
      )}
      {/*
        ⚠ Süreli bağlantı KULLANICIYA da söyleniyor: bağlantıyı kopyalayıp paylaşan biri,
        onun ne kadar yaşadığını bilmeli.
      */}
      {indirilebilir && (
        <p className="mt-3 text-xs text-fg-faint">
          İndirme bağlantısı kısa süre geçerlidir; her indirmede yeniden istenir.
        </p>
      )}
    </Card>
  );
}

/**
 * Başlıktaki düzey özeti — SİZE AÇILAN İZİNLERDEN türetiliyor.
 *
 * ### ⚠ EKRAN TEK SESLE KONUŞUR
 *
 * Bu rozet eskiden katılım satırının `accessLevel`'ını basıyordu ve ekranda İKİ ayrı
 * düzey beyanı oluyordu: başlıkta "Görüntüleme", hemen altındaki nesne satırında
 * "Görüntüleme · Yorum". İkisi ayrı kaynaktan geldiği için çelişebiliyorlardı — ve
 * çelişiyorlardı: `accessLevel` sahibin BEYANI, izinler ise size fiilen açılan şey.
 *
 * Tek gerçek kaynak fiilen açılan izinler: kullanıcının yapabildiği şey o. Rozet artık
 * onların birleşiminden türüyor, yani başlık ile satırlar aynı gerçeği söylüyor.
 *
 * ⚠ LİSTE EKRANI HÂLÂ `accessLevel` GÖSTERİYOR ve bu bir tutarsızlık DEĞİL: liste
 * yükünde (`SharedContractView`) nesne izinleri YOK — orada türetilecek bir şey yok.
 * Liste sahibin beyanını gösteriyor ve metni de öyle diyor ("Paylaşım düzeyi"). Bunu
 * "düzelt"meye kalkan biri, olmayan veriyi uydurmak zorunda kalır.
 *
 * ⚠ Hiç nesne paylaşılmamışsa rozet HİÇ ÇİZİLMİYOR: özetlenecek bir izin yok ve
 * "Görüntüleme" yazmak, aslında hiçbir şey açılmamışken bir şey açılmış gibi gösterirdi.
 */
function fiiliDuzey(nesneler: readonly SharedObjectView[]): string {
  const izinler = new Set(nesneler.flatMap((n) => n.permissions));
  const adlar = OBJECT_SHARE_PERMISSIONS.filter((p) => izinler.has(p)).map(
    (p) => OBJECT_SHARE_PERMISSION_NAMES[p],
  );

  /**
   * ⚠ Bilinmeyen izin SESSİZCE DÜŞMÜYOR: sözlükte karşılığı olmayan bir izin gelirse
   * özet onu ham hâliyle taşıyor. Yutsaydık kullanıcı, kendisine açılmış bir yeteneği
   * hiç görmezdi.
   */
  const bilinmeyenler = [...izinler].filter(
    (p) => !(OBJECT_SHARE_PERMISSIONS as readonly string[]).includes(p),
  );

  const hepsi = [...adlar, ...bilinmeyenler];
  return `Size açılan: ${hepsi.join(" · ")}`;
}

/**
 * Paylaşılan bir SÜRÜMÜN maddeleri.
 *
 * ⚠ Yalnız `version` türünde çiziliyor: madde/dosya/çalışma-kalemi paylaşımlarının içerik
 * şekli farklı ve her biri kendi sunumunu ister. Hepsini tek bileşene sıkıştırmak, hiçbirini
 * doğru göstermemek olurdu.
 *
 * ⚠ Yapı DEFANSİF okunuyor — `content` sunucudan gelen serbest bir kayıt ve şekli adaptörün
 * beyaz listesine bağlı. Beklenmedik şekilde madde listesi BOŞ kalıyor ve ekran sessizce
 * hiçbir şey çizmiyor; uydurma bir metin göstermek, kullanıcıya olmayan bir sözleşme
 * maddesi okutmak olurdu.
 */
function PaylasilanMaddeler({ nesne }: { nesne: SharedObjectView }) {
  if (nesne.objectType !== "version") return null;

  const belge = (nesne.content as { document?: unknown }).document as
    { clauses?: unknown } | null | undefined;
  const maddeler = Array.isArray(belge?.clauses) ? (belge.clauses as ContractClause[]) : [];
  if (maddeler.length === 0) return null;

  return <ClauseReadOnlyList maddeler={maddeler} testId="paylasilan-maddeler" />;
}

/**
 * Paylaşılan nesne türlerinin ekran adları.
 *
 * ⚠ Tam kapsamalı `Record`: `SHAREABLE_OBJECT_TYPES`'a yeni tür eklendiğinde derlenmiyor.
 * Yedek metinli bir arama (`?? tür`) yazsaydık yeni tür ekranda ham enum adıyla görünür,
 * kimse fark etmezdi.
 */
const PAYLASIM_TURU_ADLARI: Record<
  SharedContractDetailView["sharedObjects"][number]["objectType"],
  string
> = {
  version: "Sözleşme versiyonu",
  clause: "Madde",
  work_item: "Not / görev",
  file: "Dosya",
  revision: "Revizyon önerisi",
  negotiation: "Müzakere",
};
